import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import API from '../api';
import { toast } from 'react-toastify';
import { useDebounce } from '../hooks/useDebounce';
import { usePharmacy } from '../context/PharmacyContext';

export default function MedicinesPage() {
  const getCache = (key, defaultVal) => {
    const cached = sessionStorage.getItem(`medicinesPage_${key}`);
    return cached ? JSON.parse(cached) : defaultVal;
  };

  const [medicines, setMedicines] = useState(() => getCache('medicines', []));
  const [parties, setParties] = useState(() => getCache('parties', []));
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState(() => getCache('search', ''));
  const debouncedSearch = useDebounce(search, 250);
  const [categoryFilter, setCategoryFilter] = useState(() => getCache('categoryFilter', 'all'));
  const [stockFilter, setStockFilter] = useState(() => getCache('stockFilter', 'all'));
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMedicineBatches, setSelectedMedicineBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  const [showPrintPanel, setShowPrintPanel] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printOptions, setPrintOptions] = useState(() => getCache('printOptions', {
    groupByCategory: true,
    showPricing: true,
    showStock: true,
    highlightAlerts: true,
    orientation: 'landscape'
  }));
  const printPanelRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  const formRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (printPanelRef.current && !printPanelRef.current.contains(event.target)) {
        setShowPrintPanel(false);
      }
    };
    if (showPrintPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPrintPanel]);

  useEffect(() => {
    sessionStorage.setItem('medicinesPage_search', JSON.stringify(search));
  }, [search]);

  useEffect(() => {
    sessionStorage.setItem('medicinesPage_categoryFilter', JSON.stringify(categoryFilter));
  }, [categoryFilter]);

  useEffect(() => {
    sessionStorage.setItem('medicinesPage_stockFilter', JSON.stringify(stockFilter));
  }, [stockFilter]);

  useEffect(() => {
    sessionStorage.setItem('medicinesPage_printOptions', JSON.stringify(printOptions));
  }, [printOptions]);

  const stats = useMemo(() => {
    const total = medicines.length;
    const lowStock = medicines.filter(m => m.quantity < (m.minStockLevel || 10)).length;
    const today = new Date();
    const nextMonth = new Date();
    nextMonth.setDate(today.getDate() + 30);
    const nearExpiry = medicines.filter(m => {
      const expiry = new Date(m.expiryDate);
      return expiry >= today && expiry <= nextMonth;
    }).length;
    const expired = medicines.filter(m => {
      const expiry = new Date(m.expiryDate);
      return expiry < today;
    }).length;
    const prescription = medicines.filter(m => m.prescription).length;
    const totalValue = medicines.reduce((sum, m) => sum + (m.sellingPricePerUnit * m.quantity), 0);

    return { totalMedicines: total, lowStock, nearExpiry, expired, prescription, totalValue };
  }, [medicines]);

  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: 'Tablet',
    composition: '',
    strength: '',
    manufacturer: '',
    packaging: {
      unitsPerStrip: 10,
      stripsPerBox: '',
      volumePerBottle: '',
      volumeUnit: 'ml',
      weightPerTube: '',
      weightUnit: 'gm',
      ampouleVolume: '',
      ampouleUnit: 'ml',
      ampoulesPerBox: '',
    },
    quantity: '',
    quantityUnit: 'units',
    minStockLevel: '10',
    mrpPerUnit: '',
    purchasePricePerUnit: '',
    sellingPricePerUnit: '',
    discount: '0',
    expiryDate: '',
    batchNumber: '',
    rackNumber: '',
    prescription: false,
    party: '',
  });

  const categories = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Ointment', 'Cream', 'Drops', 'Inhaler', 'Powder', 'Suspension', 'Other'];

  const fmt = useCallback((num) =>
    Number.isFinite(Number(num))
      ? Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00', []);

  useEffect(() => {
    if (showForm && formRef.current) {
      setTimeout(() => {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [showForm]);

  const getUnitLabel = useCallback((category) => {
    switch (category) {
      case 'Tablet':
      case 'Capsule':
        return 'tablets';
      case 'Syrup':
      case 'Suspension':
      case 'Drops':
        return 'ml';
      case 'Ointment':
      case 'Cream':
        return 'gm';
      case 'Injection':
        return 'ampoules';
      default:
        return 'units';
    }
  }, []);

  const getPricingLabel = useCallback((category) => {
    switch (category) {
      case 'Tablet':
      case 'Capsule':
        return 'per Strip';
      case 'Syrup':
      case 'Suspension':
      case 'Drops':
        return 'per Bottle';
      case 'Ointment':
      case 'Cream':
        return 'per Tube';
      case 'Injection':
        return 'per Ampoule';
      default:
        return 'per Unit';
    }
  }, []);

  const { pharmacyDetails } = usePharmacy();

  const fetchMedicines = useCallback(async () => {
    try {
      if (!sessionStorage.getItem('medicinesPage_medicines')) setLoading(true);
      const params = { limit: 1000 };
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await API.get('/medicines', { params });
      setMedicines(res.data);
      sessionStorage.setItem('medicinesPage_medicines', JSON.stringify(res.data));
    } catch (err) {
      toast.error('Failed to load medicines');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, debouncedSearch]);

  const fetchParties = useCallback(async () => {
    try {
      const res = await API.get('/parties', { params: { limit: 1000 } });
      setParties(res.data);
      sessionStorage.setItem('medicinesPage_parties', JSON.stringify(res.data));
    } catch (err) {
    }
  }, []);

  useEffect(() => {
    if (parties.length === 0) {
      fetchParties();
    }
  }, [fetchParties, parties.length]);

  useEffect(() => {
    fetchMedicines();
  }, [fetchMedicines]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('packaging.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        packaging: { ...prev.packaging, [field]: value }
      }));
    } else if (name === 'category') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        quantityUnit: getUnitLabel(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  }, [getUnitLabel]);

  const handleNextStep = useCallback(() => {
    if (activeTab === 'basic') {
      if (!formData.name.trim()) {
        toast.warning('Please enter Medicine Name');
        return;
      }
      if (!formData.category) {
        toast.warning('Please select a Category');
        return;
      }
      if (!formData.party) {
        toast.warning('Please select a Supplier');
        return;
      }
      setActiveTab('packaging');
    } else if (activeTab === 'packaging') {
      setActiveTab('stock');
    }
  }, [activeTab, formData.name, formData.category, formData.party]);

  const handleSubmit = useCallback(async () => {
    if (!formData.name || !formData.party || !formData.sellingPricePerUnit || !formData.quantity) {
      toast.warning('Please fill all required fields (Name, Supplier, Selling Price, Quantity)');
      return;
    }

    try {
      setLoading(true);
      const payload = { ...formData };

      if (payload.packaging) {
        Object.keys(payload.packaging).forEach(key => {
          if (payload.packaging[key] === '' || payload.packaging[key] === null) {
            delete payload.packaging[key];
          }
        });
      }

      if (editingId) {
        await API.put(`/medicines/${editingId}`, payload);
        toast.success('Medicine updated successfully');
      } else {
        await API.post('/medicines', payload);
        toast.success('Medicine added successfully');
      }

      resetForm();
      setShowForm(false);
      fetchMedicines();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save medicine');
    } finally {
      setLoading(false);
    }
  }, [formData, editingId, fetchMedicines]);

  const handleEdit = useCallback((med) => {
    setEditingId(med._id);
    setFormData({
      name: med.name,
      brand: med.brand || '',
      category: med.category,
      composition: med.composition || '',
      strength: med.strength || '',
      manufacturer: med.manufacturer || '',
      packaging: {
        unitsPerStrip: med.packaging?.unitsPerStrip || 10,
        stripsPerBox: med.packaging?.stripsPerBox || '',
        volumePerBottle: med.packaging?.volumePerBottle || '',
        volumeUnit: med.packaging?.volumeUnit || 'ml',
        weightPerTube: med.packaging?.weightPerTube || '',
        weightUnit: med.packaging?.weightUnit || 'gm',
        ampouleVolume: med.packaging?.ampouleVolume || '',
        ampouleUnit: med.packaging?.ampouleUnit || 'ml',
        ampoulesPerBox: med.packaging?.ampoulesPerBox || '',
      },
      quantity: med.quantity,
      quantityUnit: med.quantityUnit,
      minStockLevel: med.minStockLevel || '10',
      mrpPerUnit: med.mrpPerUnit || '',
      purchasePricePerUnit: med.purchasePricePerUnit || '',
      sellingPricePerUnit: med.sellingPricePerUnit,
      discount: med.discount || '0',
      expiryDate: med.expiryDate ? med.expiryDate.split('T')[0] : '',
      batchNumber: med.batchNumber || '',
      rackNumber: med.rackNumber || '',
      prescription: med.prescription || false,
      party: med.party?._id || '',
    });

    setShowForm(true);
    setShowDetailModal(false);
    setActiveTab('basic');
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this medicine?')) return;

    try {
      await API.delete(`/medicines/${id}`);
      toast.success('Medicine deleted successfully');
      setShowDetailModal(false);
      setSelectedMedicine(null);
      fetchMedicines();
    } catch (err) {
      toast.error('Failed to delete medicine');
    }
  }, [fetchMedicines]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '', brand: '', category: 'Tablet', composition: '', strength: '', manufacturer: '',
      packaging: {
        unitsPerStrip: 10, stripsPerBox: '', volumePerBottle: '', volumeUnit: 'ml',
        weightPerTube: '', weightUnit: 'gm', ampouleVolume: '', ampouleUnit: 'ml', ampoulesPerBox: ''
      },
      quantity: '', quantityUnit: 'tablets', minStockLevel: '10',
      mrpPerUnit: '', purchasePricePerUnit: '', sellingPricePerUnit: '', discount: '0', expiryDate: '',
      batchNumber: '', rackNumber: '', prescription: false, party: '',
    });
    setEditingId(null);
    setActiveTab('basic');
  }, []);

  const getStockDisplay = useCallback((med) => {
    switch (med.category) {
      case 'Tablet':
      case 'Capsule':
        const strips = Math.floor(med.quantity / (med.packaging?.unitsPerStrip || 10));
        const remaining = med.quantity % (med.packaging?.unitsPerStrip || 10);
        return `${strips} strips ${remaining > 0 ? `+ ${remaining}` : ''}`;
      default:
        return `${med.quantity} ${med.quantityUnit}`;
    }
  }, []);

  const handleMedicineClick = useCallback(async (med) => {
    setSelectedMedicine(med);
    setShowDetailModal(true);
    setLoadingBatches(true);
    try {
      const res = await API.get(`/medicines/${med._id}/batches?active=true`);
      setSelectedMedicineBatches(res.data || []);
    } catch (err) {
      toast.error('Failed to load medicine batches');
      setSelectedMedicineBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  const getExpiryStatus = useCallback((expiryDate) => {
    if (!expiryDate) return { status: 'unknown', text: 'Not set', class: '' };
    const daysToExpiry = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysToExpiry < 0) return { status: 'expired', text: `Expired ${Math.abs(daysToExpiry)} days ago`, class: 'expired' };
    if (daysToExpiry <= 30) return { status: 'expiring', text: `Expires in ${daysToExpiry} days`, class: 'expiring' };
    if (daysToExpiry <= 90) return { status: 'warning', text: `Expires in ${daysToExpiry} days`, class: 'warning' };
    return { status: 'ok', text: `Expires in ${daysToExpiry} days`, class: 'ok' };
  }, []);

  const filteredMedicines = useMemo(() => {
    return medicines.filter(med => {
      const matchesSearch = search === '' ||
        med.name.toLowerCase().includes(search.toLowerCase()) ||
        med.brand?.toLowerCase().includes(search.toLowerCase()) ||
        med.composition?.toLowerCase().includes(search.toLowerCase());

      const matchesStock = stockFilter === 'all' ||
        (stockFilter === 'low' && med.quantity < (med.minStockLevel || 10)) ||
        (stockFilter === 'normal' && med.quantity >= (med.minStockLevel || 10));

      return matchesSearch && matchesStock;
    });
  }, [medicines, search, stockFilter]);

  const groupMedicinesByCategory = useCallback((medsList) => {
    const groups = {};
    medsList.forEach(med => {
      if (!groups[med.category]) {
        groups[med.category] = {
          medicines: [],
          totalStock: 0,
          totalValue: 0,
          lowStockCount: 0,
          expiringCount: 0
        };
      }
      groups[med.category].medicines.push(med);
      groups[med.category].totalStock += med.quantity || 0;
      groups[med.category].totalValue += (med.sellingPricePerUnit || 0) * (med.quantity || 0);
      if (med.quantity < (med.minStockLevel || 10)) groups[med.category].lowStockCount++;
      const daysToExpiry = med.expiryDate ? Math.ceil((new Date(med.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
      if (daysToExpiry !== null && daysToExpiry <= 30) groups[med.category].expiringCount++;
    });
    return groups;
  }, []);

  const handleQuickPrintAll = useCallback(() => {
    setIsPrinting(true);
    setTimeout(() => {
      handlePrintAllMedicines();
      setIsPrinting(false);
      setShowPrintPanel(false);
    }, 300);
  }, [filteredMedicines, groupMedicinesByCategory, printOptions, pharmacyDetails, categoryFilter, stockFilter, search, fmt]);

  const handlePrintAllMedicines = useCallback(() => {
    const groupedMeds = groupMedicinesByCategory(filteredMedicines);
    const sortedCategories = Object.keys(groupedMeds).sort();

    const printWindow = window.open('', '_blank');

    const totalValue = filteredMedicines.reduce((sum, m) => sum + ((m.sellingPricePerUnit || 0) * (m.quantity || 0)), 0);
    const lowStockCount = filteredMedicines.filter(m => m.quantity < (m.minStockLevel || 10)).length;
    const today = new Date();
    const expiringCount = filteredMedicines.filter(m => {
      const daysToExpiry = m.expiryDate ? Math.ceil((new Date(m.expiryDate) - today) / (1000 * 60 * 60 * 24)) : null;
      return daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 30;
    }).length;
    const expiredCount = filteredMedicines.filter(m => {
      const daysToExpiry = m.expiryDate ? Math.ceil((new Date(m.expiryDate) - today) / (1000 * 60 * 60 * 24)) : null;
      return daysToExpiry !== null && daysToExpiry < 0;
    }).length;
    const rxCount = filteredMedicines.filter(m => m.prescription).length;

    let filterText = 'All Medicines';
    if (categoryFilter !== 'all') filterText = `${categoryFilter} Only`;
    if (stockFilter !== 'all') filterText += ` • ${stockFilter === 'low' ? 'Low Stock' : 'Normal Stock'}`;
    if (search) filterText += ` • Search: "${search}"`;

    let categorySectionsHTML = '';

    if (printOptions.groupByCategory) {
      sortedCategories.forEach((category, catIndex) => {
        const group = groupedMeds[category];

        let medicineRowsHTML = '';
        group.medicines.forEach((med, medIndex) => {
          const isLowStock = med.quantity < (med.minStockLevel || 10);
          const daysToExpiry = med.expiryDate ? Math.ceil((new Date(med.expiryDate) - today) / (1000 * 60 * 60 * 24)) : null;
          const isExpiring = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 30;
          const isExpired = daysToExpiry !== null && daysToExpiry < 0;

          const alertClass = printOptions.highlightAlerts
            ? (isExpired ? 'expired-row' : isExpiring ? 'expiring-row' : isLowStock ? 'low-stock-row' : '')
            : '';

          medicineRowsHTML += `
            <tr class="${medIndex % 2 === 0 ? 'even-row' : 'odd-row'} ${alertClass}">
              <td class="cell">${medIndex + 1}</td>
              <td class="cell">
                <strong>${med.name}</strong>
                ${med.brand ? `<br><span class="sub-text">${med.brand}</span>` : ''}
                ${med.prescription ? '<span class="rx-badge">Rx</span>' : ''}
              </td>
              <td class="cell">${med.strength || '-'}</td>
              <td class="cell">${med.manufacturer || '-'}</td>
              ${printOptions.showStock ? `
                <td class="cell center ${isLowStock && printOptions.highlightAlerts ? 'alert-text' : ''}">
                  <strong>${med.quantity}</strong>
                  <br><span class="sub-text">${med.quantityUnit}</span>
                  ${isLowStock && printOptions.highlightAlerts ? '<br><span class="alert-badge low">Low</span>' : ''}
                </td>
              ` : ''}
              ${printOptions.showPricing ? `
                <td class="cell right">Rs${fmt(med.mrpPerUnit || 0)}</td>
                <td class="cell right"><strong>Rs${fmt(med.sellingPricePerUnit || 0)}</strong></td>
              ` : ''}
              <td class="cell center ${(isExpired || isExpiring) && printOptions.highlightAlerts ? 'alert-text' : ''}">
                ${med.expiryDate
              ? `${new Date(med.expiryDate).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })}
                     ${isExpired && printOptions.highlightAlerts ? '<br><span class="alert-badge expired">Expired</span>' : ''}
                     ${isExpiring && !isExpired && printOptions.highlightAlerts ? `<br><span class="alert-badge expiring">${daysToExpiry}d</span>` : ''}`
              : '-'}
              </td>
              <td class="cell">${med.rackNumber || '-'}</td>
            </tr>
          `;
        });

        categorySectionsHTML += `
          <div class="category-section">
            <div class="category-header">
              <div class="category-title">${category}</div>
              <div class="category-stats">
                <span>${group.medicines.length} items</span>
                <span>Stock Value: Rs${fmt(group.totalValue)}</span>
                ${group.lowStockCount > 0 ? `<span class="stat-alert">${group.lowStockCount} low stock</span>` : ''}
              </div>
            </div>
            
            <table class="medicines-table">
              <thead>
                <tr>
                  <th class="th" width="4%">#</th>
                  <th class="th" width="${printOptions.showPricing ? '18%' : '25%'}">Medicine</th>
                  <th class="th" width="8%">Strength</th>
                  <th class="th" width="12%">Manufacturer</th>
                  ${printOptions.showStock ? '<th class="th center" width="10%">Stock</th>' : ''}
                  ${printOptions.showPricing ? '<th class="th right" width="10%">MRP</th><th class="th right" width="10%">Price</th>' : ''}
                  <th class="th center" width="10%">Expiry</th>
                  <th class="th" width="8%">Location</th>
                </tr>
              </thead>
              <tbody>${medicineRowsHTML}</tbody>
            </table>
          </div>
        `;
      });
    } else {
      let medicineRowsHTML = '';
      filteredMedicines.forEach((med, index) => {
        const isLowStock = med.quantity < (med.minStockLevel || 10);
        const daysToExpiry = med.expiryDate ? Math.ceil((new Date(med.expiryDate) - today) / (1000 * 60 * 60 * 24)) : null;
        const isExpiring = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 30;
        const isExpired = daysToExpiry !== null && daysToExpiry < 0;

        const alertClass = printOptions.highlightAlerts
          ? (isExpired ? 'expired-row' : isExpiring ? 'expiring-row' : isLowStock ? 'low-stock-row' : '')
          : '';

        medicineRowsHTML += `
          <tr class="${index % 2 === 0 ? 'even-row' : 'odd-row'} ${alertClass}">
            <td class="cell">${index + 1}</td>
            <td class="cell">
              <strong>${med.name}</strong>
              ${med.brand ? `<br><span class="sub-text">${med.brand}</span>` : ''}
              ${med.prescription ? '<span class="rx-badge">Rx</span>' : ''}
            </td>
            <td class="cell center">
              <span class="category-badge">${med.category}</span>
            </td>
            <td class="cell">${med.strength || '-'}</td>
            ${printOptions.showStock ? `
              <td class="cell center ${isLowStock && printOptions.highlightAlerts ? 'alert-text' : ''}">
                <strong>${med.quantity}</strong> ${med.quantityUnit}
                ${isLowStock && printOptions.highlightAlerts ? '<br><span class="alert-badge low">Low</span>' : ''}
              </td>
            ` : ''}
            ${printOptions.showPricing ? `
              <td class="cell right">Rs${fmt(med.mrpPerUnit || 0)}</td>
              <td class="cell right"><strong>Rs${fmt(med.sellingPricePerUnit || 0)}</strong></td>
            ` : ''}
            <td class="cell center ${(isExpired || isExpiring) && printOptions.highlightAlerts ? 'alert-text' : ''}">
              ${med.expiryDate
            ? `${new Date(med.expiryDate).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })}
                   ${isExpired && printOptions.highlightAlerts ? '<br><span class="alert-badge expired">Expired</span>' : ''}
                   ${isExpiring && !isExpired && printOptions.highlightAlerts ? `<br><span class="alert-badge expiring">${daysToExpiry}d</span>` : ''}`
            : '-'}
            </td>
            <td class="cell">${med.rackNumber || '-'}</td>
          </tr>
        `;
      });

      categorySectionsHTML = `
        <div class="category-section">
          <table class="medicines-table">
            <thead>
              <tr>
                <th class="th" width="4%">#</th>
                <th class="th" width="16%">Medicine</th>
                <th class="th center" width="10%">Category</th>
                <th class="th" width="8%">Strength</th>
                ${printOptions.showStock ? '<th class="th center" width="10%">Stock</th>' : ''}
                ${printOptions.showPricing ? '<th class="th right" width="10%">MRP</th><th class="th right" width="10%">Price</th>' : ''}
                <th class="th center" width="10%">Expiry</th>
                <th class="th" width="8%">Location</th>
              </tr>
            </thead>
            <tbody>${medicineRowsHTML}</tbody>
          </table>
        </div>
      `;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Medicine Inventory - ${pharmacyDetails.name}</title>
        <style>
          @page { size: ${printOptions.orientation === 'landscape' ? 'A4 landscape' : 'A4 portrait'}; margin: 8mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            color: #1e293b;
            font-size: 9px;
            line-height: 1.4;
            background: #fff;
          }
          .report-header {
            text-align: center;
            padding-bottom: 12px;
            border-bottom: 3px solid #1880c2;
            margin-bottom: 16px;
          }
          .pharmacy-name {
            font-size: 22px;
            font-weight: 800;
            color: #1880c2;
            text-transform: uppercase;
            margin-bottom: 2px;
          }
          .pharmacy-info { color: #64748b; font-size: 9px; margin: 1px 0; }
          .report-title {
            font-size: 14px;
            font-weight: 700;
            color: #1e293b;
            margin-top: 8px;
            padding: 5px 14px;
            background: #f1f5f9;
            display: inline-block;
            border-radius: 14px;
          }
          .report-meta {
            display: flex;
            justify-content: center;
            gap: 16px;
            margin-top: 8px;
            font-size: 9px;
            color: #64748b;
          }
          .meta-value { font-weight: 700; color: #1e293b; }
          .summary-section {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            gap: 8px;
            margin-bottom: 16px;
            padding: 12px;
            background: #f8fafc;
            border-radius: 10px;
            border: 1px solid #e2e8f0;
          }
          .summary-card {
            text-align: center;
            padding: 10px 6px;
            background: white;
            border-radius: 8px;
          }
          .summary-value { font-size: 14px; font-weight: 800; color: #1880c2; }
          .summary-label { font-size: 8px; color: #64748b; text-transform: uppercase; }
          .category-section {
            margin-bottom: 16px;
            background: white;
            border-radius: 10px;
            border: 1px solid #e2e8f0;
            overflow: hidden;
            page-break-inside: avoid;
          }
          .category-header {
            background: #1880c2;
            padding: 10px 14px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
          }
          .category-title {
            color: white;
            font-size: 12px;
            font-weight: 700;
          }
          .category-stats {
            display: flex;
            gap: 12px;
            color: rgba(255,255,255,0.9);
            font-size: 9px;
          }
          .medicines-table { width: 100%; border-collapse: collapse; }
          .th {
            padding: 8px 6px;
            text-align: left;
            font-size: 8px;
            font-weight: 700;
            color: #64748b;
            background: #f8fafc;
            text-transform: uppercase;
            border-bottom: 2px solid #e2e8f0;
          }
          .th.center { text-align: center; }
          .th.right { text-align: right; }
          .cell {
            padding: 6px;
            font-size: 9px;
            color: #1e293b;
            border-bottom: 1px solid #f1f5f9;
            vertical-align: top;
          }
          .cell.center { text-align: center; }
          .cell.right { text-align: right; }
          .even-row { background: #fff; }
          .odd-row { background: #fafafa; }
          .low-stock-row { background: #fffbeb !important; }
          .expiring-row { background: #fff7ed !important; }
          .expired-row { background: #fef2f2 !important; }
          .sub-text { font-size: 8px; color: #64748b; }
          .alert-text { color: #dc2626 !important; }
          .rx-badge {
            display: inline-block;
            background: #f3e8ff;
            color: #7c3aed;
            padding: 1px 4px;
            border-radius: 4px;
            font-size: 7px;
            font-weight: 700;
            margin-left: 4px;
          }
          .category-badge {
            display: inline-block;
            background: #e0f2fe;
            color: #1880c2;
            padding: 2px 6px;
            border-radius: 6px;
            font-size: 8px;
            font-weight: 600;
          }
          .alert-badge {
            display: inline-block;
            padding: 1px 5px;
            border-radius: 6px;
            font-size: 7px;
            font-weight: 700;
          }
          .alert-badge.low { background: #fef3c7; color: #d97706; }
          .alert-badge.expiring { background: #ffedd5; color: #ea580c; }
          .alert-badge.expired { background: #fee2e2; color: #dc2626; }
          .report-footer {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px dashed #e2e8f0;
            text-align: center;
            color: #94a3b8;
            font-size: 8px;
          }
          @media print {
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .category-section { break-inside: avoid; }
            .medicines-table tr { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="pharmacy-name">${pharmacyDetails.name}</div>
          ${pharmacyDetails.address ? `<div class="pharmacy-info">${pharmacyDetails.address}</div>` : ''}
          ${pharmacyDetails.phone ? `<div class="pharmacy-info">${pharmacyDetails.phone} ${pharmacyDetails.email ? `• ${pharmacyDetails.email}` : ''}</div>` : ''}
          ${pharmacyDetails.pan ? `<div class="pharmacy-info"><strong>PAN: ${pharmacyDetails.pan}</strong></div>` : ''}
          <div class="report-title">Medicine Inventory Report</div>
          <div class="report-meta">
            <span>Filter: <span class="meta-value">${filterText}</span></span>
            <span>Generated: <span class="meta-value">${new Date().toLocaleString('en-IN')}</span></span>
          </div>
        </div>

        <div class="summary-section">
          <div class="summary-card">
            <div class="summary-value">${filteredMedicines.length}</div>
            <div class="summary-label">Total Items</div>
          </div>
          <div class="summary-card">
            <div class="summary-value">Rs${(totalValue / 1000).toFixed(1)}K</div>
            <div class="summary-label">Stock Value</div>
          </div>
          <div class="summary-card">
            <div class="summary-value">${sortedCategories.length}</div>
            <div class="summary-label">Categories</div>
          </div>
          <div class="summary-card">
            <div class="summary-value" style="color: #d97706;">${lowStockCount}</div>
            <div class="summary-label">Low Stock</div>
          </div>
          <div class="summary-card">
            <div class="summary-value" style="color: #ea580c;">${expiringCount}</div>
            <div class="summary-label">Expiring Soon</div>
          </div>
          <div class="summary-card">
            <div class="summary-value" style="color: #7c3aed;">${rxCount}</div>
            <div class="summary-label">Rx Required</div>
          </div>
        </div>

        ${categorySectionsHTML}

        <div class="report-footer">
          <p>Generated on ${new Date().toLocaleString('en-IN')} • ${pharmacyDetails.name}</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  }, [filteredMedicines, groupMedicinesByCategory, printOptions, pharmacyDetails, categoryFilter, stockFilter, search, fmt]);

  const styles = useMemo(() => ({
    container: { minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Inter', sans-serif" },
    header: {
      background: '#ffffff',
      borderBottom: '1px solid #e1e8ed',
      padding: isMobile ? '16px' : '20px 32px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? 16 : 0,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
    },
    headerTitle: { display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 },
    headerIcon: {
      width: isMobile ? 36 : 40,
      height: isMobile ? 36 : 40,
      background: '#1880c2',
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.5px'
    },
    headerText: { fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#1a202c', letterSpacing: '-0.3px' },
    headerSubtext: { fontSize: 12, color: '#718096', fontWeight: 500, marginTop: 2 },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      width: isMobile ? '100%' : 'auto',
      flexWrap: 'wrap',
      position: 'relative'
    },
    main: { maxWidth: 1400, margin: '0 auto', padding: isMobile ? '16px' : '24px 32px' },
    statsRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
      gap: isMobile ? 12 : 16,
      marginBottom: isMobile ? 20 : 24
    },
    statCard: {
      background: '#ffffff',
      borderRadius: isMobile ? 8 : 10,
      padding: isMobile ? 16 : 20,
      border: '1px solid #e1e8ed',
      display: 'flex',
      gap: 12,
      alignItems: 'center',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      position: 'relative'
    },
    statIconBox: {
      width: isMobile ? 40 : 48,
      height: isMobile ? 40 : 48,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      flexShrink: 0,
      fontWeight: 700,
      letterSpacing: '0.5px'
    },
    statContent: { flex: 1, minWidth: 0 },
    statNumber: { fontSize: isMobile ? 16 : 20, fontWeight: 800, color: '#1a202c' },
    statTitle: { fontSize: 10, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 },
    card: {
      background: '#ffffff',
      borderRadius: isMobile ? 8 : 10,
      border: '1px solid #e1e8ed',
      marginBottom: isMobile ? 16 : 24,
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
    },
    cardHeader: {
      padding: isMobile ? '12px 16px' : '16px 24px',
      borderBottom: '1px solid #e1e8ed',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 12,
      background: '#fafbfc'
    },
    cardTitle: {
      fontSize: isMobile ? 14 : 16,
      fontWeight: 700,
      color: '#1a202c',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      margin: 0
    },
    cardBody: { padding: isMobile ? 16 : 24 },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? 12 : 16
    },
    formGrid2: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: isMobile ? 12 : 16
    },
    formGrid4: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: isMobile ? 12 : 16
    },
    formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: { fontSize: 11, fontWeight: 600, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' },
    required: { color: '#dc2626' },
    input: {
      padding: '10px 14px',
      borderRadius: 6,
      border: '1px solid #e1e8ed',
      fontSize: 14,
      width: '100%',
      boxSizing: 'border-box',
      background: '#fff',
      transition: 'border-color 0.2s',
      outline: 'none'
    },
    select: {
      padding: '10px 14px',
      borderRadius: 6,
      border: '1px solid #e1e8ed',
      fontSize: 14,
      width: '100%',
      boxSizing: 'border-box',
      background: '#fff',
      cursor: 'pointer',
      outline: 'none'
    },
    btnPrimary: {
      padding: isMobile ? '10px 16px' : '10px 20px',
      background: '#1880c2',
      color: 'white',
      border: 'none',
      borderRadius: 6,
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: 14,
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      flex: isMobile ? 1 : 'none'
    },
    btnSecondary: {
      padding: isMobile ? '10px 16px' : '10px 20px',
      background: '#ffffff',
      color: '#4a5568',
      border: '1px solid #e1e8ed',
      borderRadius: 6,
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: 14,
      transition: 'all 0.2s',
      flex: isMobile ? 1 : 'none'
    },
    btnDanger: {
      padding: isMobile ? '10px 16px' : '10px 20px',
      background: '#fef2f2',
      color: '#dc2626',
      border: '1px solid #fecaca',
      borderRadius: 6,
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: 14,
      flex: isMobile ? 1 : 'none'
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      padding: isMobile ? '10px 12px' : '12px 16px',
      textAlign: 'left',
      fontSize: 11,
      fontWeight: 600,
      color: '#718096',
      background: '#fafbfc',
      borderBottom: '1px solid #e1e8ed',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    td: {
      padding: isMobile ? '10px 12px' : '12px 16px',
      fontSize: isMobile ? 13 : 14,
      color: '#1a202c',
      borderBottom: '1px solid #f7fafc'
    },
    badge: {
      padding: '4px 10px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      display: 'inline-block',
      letterSpacing: '0.3px'
    },
    badgeCategory: { background: '#e0f2fe', color: '#1880c2' },
    badgeRx: { background: '#f3e8ff', color: '#7c3aed' },
    badgeLowStock: { background: '#fef2f2', color: '#dc2626' },
    badgeExpired: { background: '#fef2f2', color: '#dc2626' },
    badgeExpiring: { background: '#fffbeb', color: '#d97706' },
    badgeSuccess: { background: '#f0fdf4', color: '#22c55e' },
    actionBtn: {
      padding: '6px 12px',
      borderRadius: 6,
      border: 'none',
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      marginRight: 8,
      transition: 'all 0.2s'
    },
    editBtn: { background: '#e0f2fe', color: '#1880c2' },
    deleteBtn: { background: '#fef2f2', color: '#dc2626' },
    viewBtn: { background: '#f0fdf4', color: '#22c55e' },
    modal: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      padding: isMobile ? 16 : 20
    },
    modalContent: {
      background: '#ffffff',
      borderRadius: isMobile ? 12 : 16,
      width: isMobile ? '100%' : isTablet ? '90%' : 'min(900px, 95%)',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
      overflow: 'hidden'
    },
    modalHeader: {
      padding: isMobile ? 16 : 24,
      borderBottom: '1px solid #e1e8ed',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: '#1880c2',
      color: 'white',
      flexShrink: 0
    },
    modalBody: { padding: isMobile ? 16 : 24, overflowY: 'auto', flex: 1 },
    modalFooter: {
      padding: isMobile ? 16 : 24,
      borderTop: '1px solid #e1e8ed',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      gap: 12,
      background: '#fafbfc',
      flexShrink: 0
    },
    mobileCard: {
      background: '#fff',
      borderRadius: 10,
      border: '1px solid #e1e8ed',
      padding: 14,
      marginBottom: 12,
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
    },
    emptyState: {
      textAlign: 'center',
      padding: isMobile ? 40 : 60,
      color: '#718096'
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #e1e8ed',
      background: '#fafbfc',
      overflowX: 'auto',
      whiteSpace: 'nowrap'
    },
    tab: {
      padding: '14px 24px',
      border: 'none',
      background: 'transparent',
      fontSize: 14,
      fontWeight: 600,
      color: '#718096',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      borderBottom: '3px solid transparent',
      transition: 'all 0.2s'
    },
    tabActive: {
      color: '#1880c2',
      background: 'white',
      borderBottomColor: '#1880c2'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: 16,
      background: '#f3e8ff',
      borderRadius: 10,
      border: '1px solid #e9d5ff',
      marginTop: 8
    },
    infoBox: {
      padding: '14px 18px',
      background: '#f4f9fd',
      border: '1px solid #bae6fd',
      borderRadius: 10,
      marginBottom: 20
    },
    sectionTitle: {
      fontSize: 15,
      fontWeight: 700,
      color: '#1e293b',
      margin: '24px 0 16px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    },
    printPanelContainer: {
      position: 'relative',
      flex: isMobile ? 1 : 'none'
    },
    printPanel: {
      position: 'absolute',
      top: 'calc(100% + 8px)',
      right: 0,
      width: isMobile ? 'calc(100vw - 32px)' : 340,
      background: '#fff',
      borderRadius: 16,
      boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
      zIndex: 1000,
      overflow: 'hidden',
      animation: 'slideDown 0.2s ease'
    },
    printPanelHeader: {
      padding: '16px 20px',
      background: '#1880c2',
      color: 'white'
    },
    printPanelTitle: {
      fontSize: 15,
      fontWeight: 700,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 8
    },
    printPanelSubtitle: {
      fontSize: 12,
      opacity: 0.9,
      marginTop: 4
    },
    printPanelBody: {
      padding: 16
    },
    printOption: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 14px',
      borderRadius: 10,
      marginBottom: 8,
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '2px solid #f1f5f9',
      background: '#fafafa'
    },
    printOptionActive: {
      borderColor: '#1880c2',
      background: '#f4f9fd'
    },
    printOptionText: {
      flex: 1
    },
    printOptionTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: '#1e293b'
    },
    printOptionDesc: {
      fontSize: 11,
      color: '#64748b'
    },
    checkboxInput: {
      width: 18,
      height: 18,
      accentColor: '#1880c2',
      cursor: 'pointer'
    },
    printPanelFooter: {
      padding: '12px 16px',
      background: '#f8fafc',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      gap: 10
    },
    badge2: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      padding: '8px 12px',
      background: '#e0f2fe',
      color: '#1880c2',
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.3px',
      flex: isMobile ? 1 : 'none'
    }
  }), [isMobile, isTablet]);

  const Badge = ({ children, type = 'default' }) => {
    const badgeColors = {
      default: { background: '#f1f5f9', color: '#475569' },
      category: { background: '#e0f2fe', color: '#1880c2' },
      rx: { background: '#f3e8ff', color: '#7c3aed' },
      lowStock: { background: '#fef2f2', color: '#dc2626' },
      expired: { background: '#fef2f2', color: '#dc2626' },
      expiring: { background: '#fffbeb', color: '#d97706' },
      success: { background: '#f0fdf4', color: '#22c55e' }
    };
    return <span style={{ ...styles.badge, ...badgeColors[type] }}>{children}</span>;
  };

  const PrintPanel = () => {
    const groupedMeds = useMemo(() => groupMedicinesByCategory(filteredMedicines), [filteredMedicines, groupMedicinesByCategory]);
    const categoriesCount = Object.keys(groupedMeds).length;

    return (
      <div style={styles.printPanel} ref={printPanelRef}>
        <div style={styles.printPanelHeader}>
          <h3 style={styles.printPanelTitle}>
            Inventory Report
          </h3>
          <p style={styles.printPanelSubtitle}>
            {filteredMedicines.length} medicines • {categoriesCount} categories
          </p>
        </div>

        <div style={styles.printPanelBody}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              onClick={() => setPrintOptions({ ...printOptions, orientation: 'portrait' })}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: 8,
                border: printOptions.orientation === 'portrait' ? '2px solid #1880c2' : '2px solid #e2e8f0',
                background: printOptions.orientation === 'portrait' ? '#f4f9fd' : '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: printOptions.orientation === 'portrait' ? '#1880c2' : '#64748b',
                transition: 'all 0.2s'
              }}
            >
              Portrait
            </button>
            <button
              onClick={() => setPrintOptions({ ...printOptions, orientation: 'landscape' })}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: 8,
                border: printOptions.orientation === 'landscape' ? '2px solid #1880c2' : '2px solid #e2e8f0',
                background: printOptions.orientation === 'landscape' ? '#f4f9fd' : '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: printOptions.orientation === 'landscape' ? '#1880c2' : '#64748b',
                transition: 'all 0.2s'
              }}
            >
              Landscape
            </button>
          </div>

          <div
            style={{ ...styles.printOption, ...(printOptions.groupByCategory ? styles.printOptionActive : {}) }}
            onClick={() => setPrintOptions({ ...printOptions, groupByCategory: !printOptions.groupByCategory })}
          >
            <div style={styles.printOptionText}>
              <div style={styles.printOptionTitle}>Group by Category</div>
              <div style={styles.printOptionDesc}>Organize by medicine type</div>
            </div>
            <input type="checkbox" checked={printOptions.groupByCategory} onChange={() => { }} style={styles.checkboxInput} />
          </div>

          <div
            style={{ ...styles.printOption, ...(printOptions.showPricing ? styles.printOptionActive : {}) }}
            onClick={() => setPrintOptions({ ...printOptions, showPricing: !printOptions.showPricing })}
          >
            <div style={styles.printOptionText}>
              <div style={styles.printOptionTitle}>Show Pricing</div>
              <div style={styles.printOptionDesc}>Include MRP & selling price</div>
            </div>
            <input type="checkbox" checked={printOptions.showPricing} onChange={() => { }} style={styles.checkboxInput} />
          </div>

          <div
            style={{ ...styles.printOption, ...(printOptions.showStock ? styles.printOptionActive : {}) }}
            onClick={() => setPrintOptions({ ...printOptions, showStock: !printOptions.showStock })}
          >
            <div style={styles.printOptionText}>
              <div style={styles.printOptionTitle}>Show Stock Levels</div>
              <div style={styles.printOptionDesc}>Display current quantities</div>
            </div>
            <input type="checkbox" checked={printOptions.showStock} onChange={() => { }} style={styles.checkboxInput} />
          </div>

          <div
            style={{ ...styles.printOption, ...(printOptions.highlightAlerts ? styles.printOptionActive : {}) }}
            onClick={() => setPrintOptions({ ...printOptions, highlightAlerts: !printOptions.highlightAlerts })}
          >
            <div style={styles.printOptionText}>
              <div style={styles.printOptionTitle}>Highlight Alerts</div>
              <div style={styles.printOptionDesc}>Show low stock & expiring</div>
            </div>
            <input type="checkbox" checked={printOptions.highlightAlerts} onChange={() => { }} style={styles.checkboxInput} />
          </div>
        </div>

        <div style={styles.printPanelFooter}>
          <button style={styles.btnSecondary} onClick={() => setShowPrintPanel(false)}>
            Cancel
          </button>
          <button
            style={{ ...styles.btnPrimary, flex: 1, opacity: isPrinting ? 0.7 : 1 }}
            onClick={handleQuickPrintAll}
            disabled={isPrinting || filteredMedicines.length === 0}
            onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
          >
            {isPrinting ? (
              <>
                <span style={{
                  width: 16,
                  height: 16,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></span>
                Preparing...
              </>
            ) : (
              <>Print Report</>
            )}
          </button>
        </div>
      </div>
    );
  };

  const MobileMedicineCard = ({ med }) => {
    const isLowStock = med.quantity < (med.minStockLevel || 10);
    const daysToExpiry = med.expiryDate ? Math.ceil((new Date(med.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
    const isExpiring = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 30;
    const isExpired = daysToExpiry !== null && daysToExpiry < 0;

    return (
      <div
        style={styles.mobileCard}
        onClick={() => handleMedicineClick(med)}
        onMouseEnter={(e) => e.currentTarget.style.background = '#f4f9fd'}
        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{
              width: 44,
              height: 44,
              background: '#e0f2fe',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 700,
              color: '#1880c2'
            }}>
              {med.category.substring(0, 3).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{med.name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{med.brand || 'No brand'}</div>
              <div style={{ fontSize: 11, color: '#1880c2', marginTop: 2, fontWeight: 500 }}>Supplier: {med.party?.name || 'Not set'}</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                <Badge type="category">{med.category}</Badge>
                {med.prescription && <Badge type="rx">Rx</Badge>}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Rs{fmt(med.sellingPricePerUnit)}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          <div style={{ padding: 8, background: '#f8fafc', borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: '#64748b' }}>Stock</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: isLowStock ? '#dc2626' : '#1e293b' }}>{med.quantity}</div>
          </div>
          <div style={{ padding: 8, background: '#f8fafc', borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: '#64748b' }}>MRP</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{med.mrpPerUnit ? `Rs${fmt(med.mrpPerUnit)}` : 'N/A'}</div>
          </div>
          <div style={{ padding: 8, background: '#f8fafc', borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: '#64748b' }}>Expiry</div>
            <div style={{
              fontSize: 12,
              fontWeight: 600,
              color: isExpired ? '#dc2626' : isExpiring ? '#d97706' : '#1e293b'
            }}>
              {med.expiryDate ? new Date(med.expiryDate).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }) : 'N/A'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {isLowStock && <Badge type="lowStock">Low Stock</Badge>}
          {isExpired && <Badge type="expired">Expired</Badge>}
          {isExpiring && !isExpired && <Badge type="expiring">{daysToExpiry}d left</Badge>}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleEdit(med)}
            style={{ ...styles.actionBtn, ...styles.editBtn, flex: 1 }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#bae6fd'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#e0f2fe'}
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(med._id)}
            style={{ ...styles.actionBtn, ...styles.deleteBtn, flex: 1 }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fef2f2'}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  const MedicineDetailModal = ({ medicine, onClose }) => {
    if (!medicine) return null;

    const isLowStock = medicine.quantity < (medicine.minStockLevel || 10);
    const expiryStatus = getExpiryStatus(medicine.expiryDate);
    const profitMargin = medicine.purchasePricePerUnit
      ? ((medicine.sellingPricePerUnit - medicine.purchasePricePerUnit) / medicine.purchasePricePerUnit * 100).toFixed(1)
      : null;
    const stockValue = (medicine.sellingPricePerUnit * medicine.quantity).toFixed(2);

    return (
      <div style={styles.modal} onClick={onClose}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ fontSize: 16, fontWeight: 700, background: 'rgba(255,255,255,0.2)', padding: 12, borderRadius: 12, lineHeight: 1 }}>
                {medicine.category.substring(0, 3).toUpperCase()}
              </div>
              <div>
                <h2 style={{ margin: '0 0 4px 0', fontSize: 22, fontWeight: 700 }}>{medicine.name}</h2>
                <p style={{ margin: '0 0 12px 0', opacity: 0.9, fontSize: 14 }}>
                  {medicine.brand && <span>{medicine.brand}</span>}
                  {medicine.strength && <span> • {medicine.strength}</span>}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ ...styles.badge, background: 'rgba(255,255,255,0.2)', color: 'white' }}>{medicine.category}</span>
                  {medicine.prescription && <span style={{ ...styles.badge, background: 'rgba(255,255,255,0.25)', color: 'white' }}>Rx</span>}
                  {isLowStock && <Badge type="lowStock">Low Stock</Badge>}
                </div>
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: 36, height: 36, borderRadius: '50%', fontSize: 24, cursor: 'pointer' }}>×</button>
          </div>

          <div style={styles.modalBody}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1880c2' }}>STK</span>
                <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 4px', textTransform: 'uppercase' }}>Stock</p>
                <p style={{ fontSize: 20, fontWeight: 700, margin: 0, color: isLowStock ? '#dc2626' : '#1e293b' }}>{medicine.quantity}</p>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1880c2' }}>VAL</span>
                <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 4px', textTransform: 'uppercase' }}>Stock Value</p>
                <p style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#1e293b' }}>Rs{parseFloat(stockValue).toLocaleString()}</p>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1880c2' }}>EXP</span>
                <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 4px', textTransform: 'uppercase' }}>Expiry</p>
                <p style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
                  {medicine.expiryDate ? new Date(medicine.expiryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set'}
                </p>
              </div>
              {profitMargin && (
                <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1880c2' }}>MAR</span>
                  <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 4px', textTransform: 'uppercase' }}>Margin</p>
                  <p style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#22c55e' }}>{profitMargin}%</p>
                </div>
              )}
            </div>

            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                Pricing
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>MRP</label>
                  <p style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '4px 0 0' }}>{medicine.mrpPerUnit ? `Rs${fmt(medicine.mrpPerUnit)}` : 'N/A'}</p>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Purchase Price</label>
                  <p style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '4px 0 0' }}>{medicine.purchasePricePerUnit ? `Rs${fmt(medicine.purchasePricePerUnit)}` : 'N/A'}</p>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Selling Price</label>
                  <p style={{ fontSize: 16, fontWeight: 600, color: '#22c55e', margin: '4px 0 0' }}>Rs{fmt(medicine.sellingPricePerUnit)}</p>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Discount</label>
                  <p style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '4px 0 0' }}>{medicine.discount || 0}%</p>
                </div>
              </div>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                Additional Information
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Supplier</label>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '4px 0 0' }}>{medicine.party?.name || 'Not set'}</p>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Composition</label>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '4px 0 0' }}>{medicine.composition || 'N/A'}</p>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Manufacturer</label>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '4px 0 0' }}>{medicine.manufacturer || 'N/A'}</p>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Rack / Location</label>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '4px 0 0' }}>{medicine.rackNumber || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div style={{ background: '#ffffff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                📦 Available Batches & Suppliers
              </h3>
              {loadingBatches ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                  Loading batches...
                </div>
              ) : selectedMedicineBatches.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                  No batch details found.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#4a5568' }}>Batch #</th>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#4a5568' }}>Supplier</th>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#4a5568' }}>Expiry</th>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#4a5568', textAlign: 'right' }}>Stock Qty</th>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#4a5568', textAlign: 'right' }}>Cost / MRP / Sell</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedMedicineBatches.map((b, idx) => {
                        const isExpired = new Date(b.expiryDate) < new Date();
                        return (
                          <tr key={b._id || idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? 'transparent' : '#f8fafc' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b' }}>{b.batchNumber}</td>
                            <td style={{ padding: '10px 12px', color: '#4a5568' }}>{b.party?.name || 'Unknown / Legacy'}</td>
                            <td style={{ padding: '10px 12px', color: isExpired ? '#dc2626' : '#1e293b', fontWeight: isExpired ? '600' : 'normal' }}>
                              {new Date(b.expiryDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                              {isExpired && <span style={{ marginLeft: 6, fontSize: 10, background: '#fee2e2', color: '#dc2626', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>Expired</span>}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: b.quantity === 0 ? '#dc2626' : '#1e293b' }}>
                              {b.quantity}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#64748b', fontSize: 12 }}>
                              Rs{fmt(b.purchasePricePerUnit)} / Rs{fmt(b.mrpPerUnit)} / <strong style={{ color: '#22c55e' }}>Rs{fmt(b.sellingPricePerUnit)}</strong>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div style={styles.modalFooter}>
            <button onClick={onClose} style={styles.btnSecondary}>Close</button>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
              <button onClick={() => handleDelete(medicine._id)} style={styles.btnDanger}>Delete</button>
              <button
                onClick={() => handleEdit(medicine)}
                style={styles.btnPrimary}
                onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.headerTitle}>
            <div style={styles.headerIcon}>MED</div>
            <div>
              <div style={styles.headerText}>Medicine Inventory</div>
              <div style={styles.headerSubtext}>Manage your pharmacy stock</div>
            </div>
          </div>
          <div style={styles.headerActions}>
            <div style={styles.printPanelContainer}>
              <button
                style={{
                  ...styles.btnPrimary,
                  background: showPrintPanel ? '#126396' : '#1880c2'
                }}
                onClick={() => setShowPrintPanel(!showPrintPanel)}
                disabled={filteredMedicines.length === 0}
                onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
                onMouseLeave={(e) => e.currentTarget.style.background = showPrintPanel ? '#126396' : '#1880c2'}
              >
                Print
                <span style={{
                  marginLeft: 2,
                  transform: showPrintPanel ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s',
                  display: 'inline-block',
                  fontSize: 10
                }}>▼</span>
              </button>
              {showPrintPanel && <PrintPanel />}
            </div>

            <div style={styles.badge2}>
              {stats.totalMedicines} Items
            </div>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              style={styles.btnPrimary}
              onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
            >
              + Add Medicine
            </button>
          </div>
        </header>

        <main style={styles.main}>
          <div style={styles.statsRow}>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIconBox, background: '#e0f2fe', color: '#1880c2' }}>TOT</div>
              <div style={styles.statContent}>
                <div style={{ ...styles.statNumber, color: '#1880c2' }}>{stats.totalMedicines}</div>
                <div style={styles.statTitle}>Total Medicines</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIconBox, background: '#fef3c7', color: '#d97706' }}>LOW</div>
              <div style={styles.statContent}>
                <div style={{ ...styles.statNumber, color: '#d97706' }}>{stats.lowStock}</div>
                <div style={styles.statTitle}>Low Stock</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIconBox, background: '#fef2f2', color: '#dc2626' }}>EXP</div>
              <div style={styles.statContent}>
                <div style={{ ...styles.statNumber, color: '#dc2626' }}>{stats.nearExpiry}</div>
                <div style={styles.statTitle}>Expiring Soon</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIconBox, background: '#f3e8ff', color: '#7c3aed' }}>RX</div>
              <div style={styles.statContent}>
                <div style={{ ...styles.statNumber, color: '#7c3aed' }}>{stats.prescription}</div>
                <div style={styles.statTitle}>Rx Required</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIconBox, background: '#f0fdf4', color: '#059669' }}>VAL</div>
              <div style={styles.statContent}>
                <div style={{ ...styles.statNumber, color: '#059669' }}>Rs{(stats.totalValue / 1000).toFixed(1)}K</div>
                <div style={styles.statTitle}>Stock Value</div>
              </div>
            </div>
          </div>

          {showForm && (
            <div style={styles.card} ref={formRef}>
              <div style={{ ...styles.modalHeader, background: '#fafbfc', color: '#1a202c' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                    {editingId ? 'Edit Medicine' : 'Add New Medicine'}
                  </h2>
                </div>
                <button
                  onClick={() => { setShowForm(false); resetForm(); }}
                  style={{ background: '#f1f5f9', border: 'none', color: '#64748b', width: 36, height: 36, borderRadius: 8, fontSize: 20, cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>

              <div style={styles.tabs}>
                <button style={{ ...styles.tab, ...(activeTab === 'basic' ? styles.tabActive : {}) }} onClick={() => setActiveTab('basic')}>
                  Basic Info
                </button>
                <button style={{ ...styles.tab, ...(activeTab === 'packaging' ? styles.tabActive : {}) }} onClick={() => { if (activeTab === 'basic') handleNextStep(); else setActiveTab('packaging'); }}>
                  Packaging
                </button>
                <button style={{ ...styles.tab, ...(activeTab === 'stock' ? styles.tabActive : {}) }} onClick={() => { if (activeTab === 'basic') handleNextStep(); else setActiveTab('stock'); }}>
                  Stock & Pricing
                </button>
              </div>

              <div style={styles.cardBody}>
                {activeTab === 'basic' && (
                  <>
                    <div style={styles.formGrid}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Medicine Name <span style={styles.required}>*</span></label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="e.g., Paracetamol"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Brand Name</label>
                        <input
                          type="text"
                          name="brand"
                          value={formData.brand}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="e.g., Crocin"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Category <span style={styles.required}>*</span></label>
                        <select name="category" value={formData.category} onChange={handleInputChange} style={styles.select}>
                          {categories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                        </select>
                      </div>
                    </div>
                    <div style={{ ...styles.formGrid, marginTop: 16 }}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Strength</label>
                        <input
                          type="text"
                          name="strength"
                          value={formData.strength}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="e.g., 500mg"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Manufacturer</label>
                        <input
                          type="text"
                          name="manufacturer"
                          value={formData.manufacturer}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="Company name"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Supplier <span style={styles.required}>*</span></label>
                        <select name="party" value={formData.party} onChange={handleInputChange} style={styles.select}>
                          <option value="">Select Supplier</option>
                          {parties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ ...styles.formGroup, marginTop: 16 }}>
                      <label style={styles.label}>Composition / Salt</label>
                      <input
                        type="text"
                        name="composition"
                        value={formData.composition}
                        onChange={handleInputChange}
                        style={styles.input}
                        placeholder="e.g., Paracetamol IP"
                        onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                        onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                      />
                    </div>
                    <div style={styles.checkbox}>
                      <input type="checkbox" name="prescription" checked={formData.prescription} onChange={handleInputChange} id="prescription" style={{ width: 20, height: 20, cursor: 'pointer' }} />
                      <label htmlFor="prescription" style={{ fontSize: 14, fontWeight: 600, color: '#7c3aed', cursor: 'pointer' }}>
                        This medicine requires a prescription
                      </label>
                    </div>
                  </>
                )}

                {activeTab === 'packaging' && (
                  <>
                    <div style={styles.infoBox}>
                      <p style={{ margin: 0, color: '#1880c2', fontWeight: 500, fontSize: 14 }}>
                        Selected Category: <strong>{formData.category}</strong>
                      </p>
                    </div>
                    {(formData.category === 'Tablet' || formData.category === 'Capsule') && (
                      <div style={styles.formGrid2}>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Tablets per Strip</label>
                          <input
                            type="number"
                            name="packaging.unitsPerStrip"
                            value={formData.packaging.unitsPerStrip}
                            onChange={handleInputChange}
                            style={styles.input}
                            placeholder="e.g., 10"
                            onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                            onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Strips per Box</label>
                          <input
                            type="number"
                            name="packaging.stripsPerBox"
                            value={formData.packaging.stripsPerBox}
                            onChange={handleInputChange}
                            style={styles.input}
                            placeholder="e.g., 10"
                            onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                            onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                          />
                        </div>
                      </div>
                    )}
                    {(formData.category === 'Syrup' || formData.category === 'Suspension' || formData.category === 'Drops') && (
                      <div style={styles.formGrid2}>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Volume per Bottle</label>
                          <input
                            type="number"
                            name="packaging.volumePerBottle"
                            value={formData.packaging.volumePerBottle}
                            onChange={handleInputChange}
                            style={styles.input}
                            placeholder="e.g., 100"
                            onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                            onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Volume Unit</label>
                          <select name="packaging.volumeUnit" value={formData.packaging.volumeUnit} onChange={handleInputChange} style={styles.select}>
                            <option value="ml">Milliliter (ml)</option>
                            <option value="L">Liter (L)</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'stock' && (
                  <>
                    <h3 style={styles.sectionTitle}>Stock Information</h3>
                    <div style={styles.formGrid4}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Quantity <span style={styles.required}>*</span></label>
                        <input
                          type="number"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="0"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                        <small style={{ color: '#64748b', marginTop: 4, display: 'block', fontSize: 12 }}>In {formData.quantityUnit}</small>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Min Stock Level</label>
                        <input
                          type="number"
                          name="minStockLevel"
                          value={formData.minStockLevel}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="10"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Batch Number</label>
                        <input
                          type="text"
                          name="batchNumber"
                          value={formData.batchNumber}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="BT123456"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Rack / Location</label>
                        <input
                          type="text"
                          name="rackNumber"
                          value={formData.rackNumber}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="A-12"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                    </div>
                    <div style={{ ...styles.formGrid2, marginTop: 16 }}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Expiry Date</label>
                        <input
                          type="date"
                          name="expiryDate"
                          value={formData.expiryDate}
                          onChange={handleInputChange}
                          style={styles.input}
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Discount (%)</label>
                        <input
                          type="number"
                          name="discount"
                          value={formData.discount}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="0"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                    </div>
                    <h3 style={styles.sectionTitle}>Pricing</h3>
                    <div style={styles.formGrid}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>MRP {getPricingLabel(formData.category)}</label>
                        <input
                          type="number"
                          step="0.01"
                          name="mrpPerUnit"
                          value={formData.mrpPerUnit}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="0.00"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Purchase Price {getPricingLabel(formData.category)}</label>
                        <input
                          type="number"
                          step="0.01"
                          name="purchasePricePerUnit"
                          value={formData.purchasePricePerUnit}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="0.00"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Selling Price <span style={styles.required}>*</span></label>
                        <input
                          type="number"
                          step="0.01"
                          name="sellingPricePerUnit"
                          value={formData.sellingPricePerUnit}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="0.00"
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 24, borderTop: '1px solid #e2e8f0', marginTop: 24, flexWrap: 'wrap', gap: 12 }}>
                  <button style={styles.btnSecondary} onClick={() => { setShowForm(false); resetForm(); }}>Cancel</button>
                  <div style={{ display: 'flex', gap: 12 }}>
                    {activeTab !== 'basic' && (
                      <button style={styles.btnSecondary} onClick={() => setActiveTab(activeTab === 'stock' ? 'packaging' : 'basic')}>Previous</button>
                    )}
                    {activeTab !== 'stock' ? (
                      <button
                        style={styles.btnPrimary}
                        onClick={handleNextStep}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        style={styles.btnPrimary}
                        onClick={handleSubmit}
                        disabled={loading}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
                      >
                        {loading ? 'Saving...' : (editingId ? 'Update' : 'Add Medicine')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={styles.card}>
            <div style={styles.cardBody}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: isMobile ? '100%' : 280, position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Search by name, brand, or composition..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyUp={(e) => e.key === 'Enter' && fetchMedicines()}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                    onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                  />
                </div>
                <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={{ ...styles.select, width: isMobile ? '100%' : 'auto', minWidth: 160 }}>
                  <option value="all">All Categories</option>
                  {categories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                </select>
                <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} style={{ ...styles.select, width: isMobile ? '100%' : 'auto', minWidth: 140 }}>
                  <option value="all">All Stock</option>
                  <option value="low">Low Stock</option>
                  <option value="normal">Normal Stock</option>
                </select>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#718096' }}>
                <div style={{ width: 48, height: 48, border: '4px solid #e0f2fe', borderTopColor: '#1880c2', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }}></div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Loading medicines...</div>
              </div>
            ) : filteredMedicines.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 56, marginBottom: 16, opacity: 0.3 }}>📦</div>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1e293b', margin: '0 0 8px' }}>No medicines found</h3>
                <p style={{ margin: '0 0 24px' }}>Add your first medicine to get started</p>
                <button
                  onClick={() => { resetForm(); setShowForm(true); }}
                  style={styles.btnPrimary}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
                >
                  + Add Medicine
                </button>
              </div>
            ) : isMobile ? (
              <div style={{ padding: 16 }}>
                {filteredMedicines.map(med => <MobileMedicineCard key={med._id} med={med} />)}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Medicine</th>
                      <th style={styles.th}>Category</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Stock</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Price</th>
                      <th style={styles.th}>Expiry</th>
                      <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMedicines.map(med => {
                      const isLowStock = med.quantity < (med.minStockLevel || 10);
                      const daysToExpiry = med.expiryDate ? Math.ceil((new Date(med.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                      const isExpiring = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 30;
                      const isExpired = daysToExpiry !== null && daysToExpiry < 0;

                      return (
                        <tr
                          key={med._id}
                          onClick={() => handleMedicineClick(med)}
                          style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f4f9fd'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{
                                width: 44,
                                height: 44,
                                background: '#e0f2fe',
                                borderRadius: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#1880c2'
                              }}>
                                {med.category.substring(0, 3).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: '#1e293b' }}>{med.name}</div>
                                <div style={{ fontSize: 12, color: '#64748b' }}>{med.brand || 'No brand'} {med.strength && `• ${med.strength}`}</div>
                                <div style={{ fontSize: 11, color: '#1880c2', marginTop: 2, fontWeight: 500 }}>
                                  Supplier: {med.party?.name || 'Not set'}
                                </div>
                                {med.prescription && <Badge type="rx">Rx</Badge>}
                              </div>
                            </div>
                          </td>
                          <td style={styles.td}><Badge type="category">{med.category}</Badge></td>
                          <td style={{ ...styles.td, textAlign: 'right' }}>
                            <div style={{ fontSize: 18, fontWeight: 700, color: isLowStock ? '#dc2626' : '#1e293b' }}>{med.quantity}</div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>{getStockDisplay(med)}</div>
                            {isLowStock && <Badge type="lowStock">Low</Badge>}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right' }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Rs{fmt(med.sellingPricePerUnit)}</div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>MRP: {med.mrpPerUnit ? `Rs${fmt(med.mrpPerUnit)}` : 'N/A'}</div>
                          </td>
                          <td style={styles.td}>
                            {med.expiryDate ? (
                              <>
                                <div style={{ fontSize: 14, fontWeight: 500, color: isExpired ? '#dc2626' : isExpiring ? '#d97706' : '#1e293b' }}>
                                  {new Date(med.expiryDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                                </div>
                                {isExpired && <Badge type="expired">Expired</Badge>}
                                {isExpiring && !isExpired && <Badge type="expiring">{daysToExpiry}d</Badge>}
                              </>
                            ) : (<span style={{ color: '#94a3b8' }}>Not set</span>)}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                              <button
                                style={{ ...styles.actionBtn, ...styles.viewBtn }}
                                onClick={() => handleMedicineClick(med)}
                                title="View"
                                onMouseEnter={(e) => e.currentTarget.style.background = '#dcfce7'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#f0fdf4'}
                              >
                                View
                              </button>
                              <button
                                style={{ ...styles.actionBtn, ...styles.editBtn }}
                                onClick={() => handleEdit(med)}
                                title="Edit"
                                onMouseEnter={(e) => e.currentTarget.style.background = '#bae6fd'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#e0f2fe'}
                              >
                                Edit
                              </button>
                              <button
                                style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                                onClick={() => handleDelete(med._id)}
                                title="Delete"
                                onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#fef2f2'}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {filteredMedicines.length > 0 && !loading && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 8 }}>
                <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
                  Showing <strong style={{ color: '#1e293b' }}>{filteredMedicines.length}</strong> of <strong style={{ color: '#1e293b' }}>{medicines.length}</strong> medicines
                </p>
                <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>Click on any row to view details</p>
              </div>
            )}
          </div>
        </main>

        {showDetailModal && selectedMedicine && (
          <MedicineDetailModal medicine={selectedMedicine} onClose={() => { setShowDetailModal(false); setSelectedMedicine(null); }} />
        )}
      </div>
    </>
  );
}